import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { Role } from '../../../src/admin/types';
import { useAuth } from '../auth/AuthContext';

const ROLE_RANK: Record<Role, number> = { viewer: 1, editor: 2, admin: 3 };

interface ProtectedRouteProps {
  children: ReactNode;
  minRole?: Role;
}

/**
 * Route guard.
 * - `loading` → renders nothing (avoid flicker before AuthContext settles).
 * - `anonymous` → redirect to `/auth/login`.
 * - `authenticated` but missing or below `minRole` → redirect to `/`.
 */
export function ProtectedRoute({
  children,
  minRole = 'viewer',
}: ProtectedRouteProps): JSX.Element | null {
  const { status, user } = useAuth();
  if (status === 'loading') return null;
  if (status === 'anonymous') return <Navigate to="/auth/login" replace />;
  const role = user?.role ?? null;
  if (!role || ROLE_RANK[role] < ROLE_RANK[minRole]) {
    return (
      <main className="p-6 text-sm text-gray-600">このページを表示する権限がありません。</main>
    );
  }
  return <>{children}</>;
}
